import { useState, useEffect } from "react";
import { useLogoAnimation } from "./hooks/useLogoAnimation";
import { ThemeLogo } from "./ThemeLogo";
import kartlyIcon from "../images/KARTLY-ICON.png";

export function AnimatedLogo() {
  const { isScrolled, scrollToTop, scrollProgress } = useLogoAnimation();
  const [animationPhase, setAnimationPhase] = useState<
    "icon" | "icon-to-logo" | "logo" | "scroll-complete"
  >("icon");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    // Initial animation sequence
    const timer1 = setTimeout(() => {
      setAnimationPhase("icon-to-logo");
    }, 500); // Icon shows for 500ms

    const timer2 = setTimeout(() => {
      setAnimationPhase("logo");
    }, 1500); // Transition takes 1000ms

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  // Handle scroll-triggered transition - keep full logo always
  useEffect(() => {
    if (!mounted) return;

    if (isScrolled && animationPhase === "logo") {
      // Skip logo-to-icon transition, go directly to scroll-complete with full logo
      setAnimationPhase("scroll-complete");
    } else if (!isScrolled && animationPhase === "scroll-complete") {
      // When scrolling back to top, go back to logo
      setAnimationPhase("logo");
    }
  }, [isScrolled, animationPhase, mounted]);

  // Calculate interpolated values for ultra-smooth animation
  const interpolatedScale = 1 - scrollProgress * 0.08;
  const interpolatedOpacity = 1 - scrollProgress * 0.1;

  if (!mounted) return null;

  return (
    <div className={`logo-container ${isScrolled ? "scrolled" : ""}`}>
      <div className="relative group">
        {/* Enhanced multi-layered glow effects */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-red-400/40 to-red-600/40 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-all duration-1000 scale-125"
          style={{
            transform: `scale(${1.25 * interpolatedScale})`,
          }}
        ></div>

        {/* Secondary glow layer with dynamic scaling */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-red-300/30 to-red-500/30 rounded-full blur-2xl opacity-20 group-hover:opacity-60 transition-all duration-800 scale-110"
          style={{
            transform: `scale(${1.1 * interpolatedScale})`,
            opacity: 0.2 * interpolatedOpacity,
          }}
        ></div>

        {/* Tertiary subtle glow */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-red-200/20 to-red-400/20 rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-all duration-600 scale-105"
          style={{
            transform: `scale(${1.05 * interpolatedScale})`,
            opacity: 0.3 * interpolatedOpacity,
          }}
        ></div>

        {/* Icon Logo - shows only during initial load animation */}
        <img
          src={kartlyIcon}
          alt="Kartly Icon"
          className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 object-contain cursor-pointer transition-all duration-800 ease-in-out kartly-icon ${
            animationPhase === "icon"
              ? "opacity-100 scale-100"
              : "opacity-0 scale-75"
          }`}
          onClick={scrollToTop}
          style={{
            filter: "drop-shadow(0 8px 32px rgba(192, 34, 40, 0.15))",
            display: animationPhase === "icon" ? "block" : "none",
          }}
        />

        {/* Full Logo - shows after icon animation and during normal states including header */}
        <div
          className={`transition-all duration-1000 ease-in-out ${
            animationPhase === "icon-to-logo" ||
            animationPhase === "logo" ||
            animationPhase === "scroll-complete"
              ? "opacity-100 scale-100"
              : "opacity-0 scale-90"
          }`}
          style={{
            // Ensure full logo appears slightly delayed during icon-to-logo transition
            transitionDelay:
              animationPhase === "icon-to-logo" ? "200ms" : "0ms",
          }}
        >
          <ThemeLogo
            alt="Kartly Logo"
            className={`relative logo-pulse interactive-glow transform-gpu ${
              animationPhase === "scroll-complete" ? "header-logo" : ""
            }`}
            onClick={scrollToTop}
            style={{
              filter: isScrolled
                ? "drop-shadow(0 4px 16px rgba(192, 34, 40, 0.12))"
                : `drop-shadow(0 ${8 + scrollProgress * 4}px ${
                    32 + scrollProgress * 8
                  }px rgba(192, 34, 40, ${
                    0.15 - scrollProgress * 0.03
                  })) drop-shadow(0 ${12 + scrollProgress * 4}px ${
                    40 + scrollProgress * 8
                  }px rgba(160, 29, 35, ${0.1 - scrollProgress * 0.02}))`,
              transform: `scale(${interpolatedScale})`,
            }}
          />
        </div>
      </div>
    </div>
  );
}
